<template>
  <div class="upload-item mb-3">
    <div class="d-flex align-items-center justify-content-between mb-2">
      <div class="d-flex align-items-center flex-1">
        <div class="me-2">
          <i class="mc-icon icon-outline_layers fs-3"></i>
        </div>
        <div class="w-100">
          <div class="upload-item__title font-weight-medium">
            {{ upload.fileName }}
          </div>
          <div class="d-flex align-items-center">
            <div class="upload-item__status small">
              <span :class="statusClass">
                {{ statusText }}
              </span>
              <template v-if="showsStatusTime">
                &bull;
                <span class="text-muted">{{ formattedStatusTime }}</span>
              </template>
            </div>
            <template v-if="showsProgress">
              <span class="px-2 mb-1 bullet text-secondary">&bull;</span>
              <div class="upload-item__progress text-sm">
                {{ upload.progress || 0 }}%
              </div>
            </template>
          </div>
          <div v-if="showsProgress" class="mt-2">
            <div class="progress" style="height: 6px">
              <div
                class="progress-bar bg-primary"
                role="progressbar"
                :style="{ width: (upload.progress || 0) + '%' }"
                :aria-valuenow="upload.progress || 0"
                aria-valuemin="0"
                aria-valuemax="100"
              ></div>
            </div>
          </div>
          <div v-if="showsError" class="small text-danger mt-1">
            {{ errorText }}
          </div>
          <div v-if="showsFileReselectionNotice" class="small text-danger mt-1">
            {{ $t('global.fileUploader.messages.selectSameFileToResume') }}
          </div>
        </div>
      </div>

      <div class="upload-item__action">
        <i
          v-if="isPaused"
          class="mc-icon icon-outline_play fs-5 cursor-pointer me-1"
          :title="$t('global.fileUploader.actions.resume')"
          @click="$emit('resume', upload)"
        ></i>
        <template v-if="upload.status === 'uploading'">
          <i
            class="mc-icon icon-outline_pause fs-5 cursor-pointer me-1"
            :title="$t('global.fileUploader.actions.pause')"
            @click="$emit('pause', upload)"
          ></i>
          <i
            class="mc-icon icon-outline_close fs-4 cursor-pointer"
            :title="$t('global.fileUploader.actions.cancel')"
            @click="$emit('cancel', upload)"
          ></i>
        </template>
        <i
          v-else-if="upload.status === 'processing'"
          class="mc-icon icon-outline_close fs-4 cursor-pointer"
          :title="$t('global.fileUploader.actions.cancel')"
          @click="$emit('cancel', upload)"
        ></i>
        <i
          v-if="isPaused"
          class="mc-icon icon-outline_close fs-4 cursor-pointer ml-2"
          :title="$t('global.fileUploader.actions.cancel')"
          @click="$emit('cancel', upload)"
        ></i>
        <i
          v-else-if="isTerminal"
          class="mc-icon icon-outline_trash text-danger fs-4 cursor-pointer"
          @click="$emit('remove', upload.id)"
        ></i>
      </div>
    </div>
  </div>
</template>

<script>
import { supportsPersistentFileHandle } from '@/services/upload/file-handle-store'

const PROGRESS_STATUSES = ['uploading', 'processing', 'paused', 'waiting_resume']
const TERMINAL_STATUSES = ['completed', 'failed', 'canceled']

export default {
  name: 'McFileUploaderItem',
  props: {
    upload: {
      type: Object,
      required: true,
    },
  },
  computed: {
    isPaused() {
      return ['waiting_resume', 'paused'].includes(this.upload.status)
    },
    isTerminal() {
      return TERMINAL_STATUSES.includes(this.upload.status)
    },
    showsProgress() {
      return (
        this.upload.progress !== undefined && PROGRESS_STATUSES.includes(this.upload.status)
      )
    },
    showsStatusTime() {
      return Boolean(this.upload.statusAt) && this.isTerminal
    },
    showsError() {
      if (this.upload.interruptionReason === 'page-refresh') return false
      return Boolean(this.upload.error || this.upload.errorKey)
    },
    showsFileReselectionNotice() {
      return (
        this.isPaused &&
        !this.upload.file &&
        !supportsPersistentFileHandle()
      )
    },
    errorText() {
      return this.upload.errorKey ? this.$t(this.upload.errorKey) : this.upload.error
    },
    statusClass() {
      const status = this.upload.status
      return {
        'text-success': status === 'completed',
        'text-danger': status === 'failed',
        'text-warning': ['canceled', 'canceling', 'paused', 'waiting_resume'].includes(status),
        'text-primary': ['uploading', 'processing'].includes(status),
      }
    },
    statusText() {
      const statusKey = `global.fileUploader.status.${this.upload.status}`
      return this.$te(statusKey) ? this.$t(statusKey) : this.upload.status
    },
    formattedStatusTime() {
      const date = new Date(this.upload.statusAt)
      if (Number.isNaN(date.getTime())) return ''

      const datePart = date.toLocaleDateString(this.$i18n.locale === 'en' ? 'en-US' : 'id-ID', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
      const timePart = `${String(date.getHours()).padStart(2, '0')}:${String(
        date.getMinutes()
      ).padStart(2, '0')}`
      return `${datePart} ${timePart}`
    },
  },
}
</script>

<style lang="scss" scoped>
.upload-item {
  padding: 12px;
  border: 1px solid $teal-100;
  border-radius: 6px;
  background-color: $white;
  position: relative;

  .d-flex {
    width: 100%;
  }
}

.upload-item__action {
  flex-shrink: 0;
  position: absolute;
  top: 15px;
  right: 15px;
}

.bullet {
  font-size: 10px;
  line-height: 1;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
